// src/components/soldLeads/footer/EmailActions.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  HStack,
  Button,
  Select,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { EmailIcon, CheckCircleIcon } from "@chakra-ui/icons";
import useOrgClaims from "../../../hooks/useOrgClaims";

// Defaults + helpers
import {
  DEFAULT_TEMPLATES,
  fill,
  buildVars,
  makeMailtoUrl,
} from "../../../lib/templates.js";

// Firestore (load per-org template overrides)
import { db } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

export default function EmailActions({
  row,
  report,
  orgId,
  onCopy,
  onForward,
  defaultTemplate = "survey",
  templateOptions,
}) {
  const { user } = useOrgClaims();
  const [templateKey, setTemplateKey] = useState(defaultTemplate);
  const [forwarded, setForwarded] = useState(false);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [orgProfile, setOrgProfile] = useState({});

  // Reset forwarded flag when the lead changes
  useEffect(() => {
    setForwarded(false);
  }, [row?.leadId]);

  // Load the organization's main profile data (for placeholders)
  useEffect(() => {
    let alive = true;
    async function fetchOrgProfile() {
      if (!orgId) {
        if (alive) setOrgProfile({});
        return;
      }
      try {
        const orgRef = doc(db, 'organizations', orgId);
        const orgSnap = await getDoc(orgRef);
        if (alive && orgSnap.exists()) {
          setOrgProfile(orgSnap.data());
        }
      } catch {
        // ignore errors
      }
    }
    fetchOrgProfile();
    return () => { alive = false; };
  }, [orgId]);


  // Load per-org templates and merge over defaults (if present)
  useEffect(() => {
    let alive = true;
    async function loadOrgTemplates() {
      try {
        if (!orgId) {
          if (alive) setTemplates(DEFAULT_TEMPLATES);
          return;
        }
        const ref = doc(db, "organizations", orgId, "settings", "templates");
        const snap = await getDoc(ref);
        if (!alive) return;

        if (snap.exists()) {
          const remote = snap.data() || {};
          const keys = ["survey", "pm", "cancelled", "callbacks"];
          const merged = keys.reduce((acc, k) => {
            const base = DEFAULT_TEMPLATES[k] || {};
            const over = remote[k] || {};
            acc[k] = { ...base, ...over };
            return acc;
          }, {});
          setTemplates(merged);
        } else {
          setTemplates(DEFAULT_TEMPLATES);
        }
      } catch {
        if (alive) setTemplates(DEFAULT_TEMPLATES);
      }
    }
    loadOrgTemplates();
    return () => {
      alive = false;
    };
  }, [orgId]);

  // Options shown in the select
  const options = templateOptions || [
    { value: "survey", label: "Request Survey" },
    { value: "pm", label: "PM" },
    { value: "cancelled", label: "Cancelled" },
    { value: "callbacks", label: "Call Again" },
  ];

  // Build placeholder variables from lead + report
  const vars = useMemo(() => {
    const leadId = row?.id || row?.leadId || '';
    const leadLink = leadId ? `${window.location.origin}/leads/${leadId}` : 'N/A';

    return buildVars({
      lead: row || {},
      org: orgProfile || {},
      report: report || {},
      extras: {
        user: user || {},
        leadLink: leadLink,
      },
    });
  }, [row, report, orgProfile, user]);


  const handleOpenEmail = () => {
    const tpl = templates?.[templateKey] || templates?.survey || {};

    const subjectSrc = tpl.emailSubjectTemplate ?? "";
    const bodySrc = tpl.emailBodyTemplate ?? "";

    const subject = fill(subjectSrc, vars).trim();
    const body = fill(bodySrc, vars).trim();

    const mailto = makeMailtoUrl(["undisclosed-recipients:;"], subject, body);
    window.location.href = mailto;
  };

  const handleForward = () => {
    setForwarded(true);
    if (typeof onForward === "function") onForward(row);
  };

  // --- MODIFIED PART ---
  return (
    <HStack spacing={4} flexWrap="wrap" alignItems="flex-end">
      {/* Grouped the template picker and email button */}
      <FormControl id="email-template">
        <FormLabel mb={1} fontSize="sm">
          Choose Template
        </FormLabel>
        <HStack spacing={2}>
          <Select
            size="sm"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            maxWidth="160px"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Button size="sm" leftIcon={<EmailIcon />} onClick={handleOpenEmail}>
            Email the Surveyor
          </Button>
        </HStack>
      </FormControl>

      {/* Forward button remains */}
      {forwarded ? (
        <Button size="sm" leftIcon={<CheckCircleIcon />} isDisabled variant="outline">
          Forwarded
        </Button>
      ) : (
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<CheckCircleIcon />}
          onClick={handleForward}
        >
          Mark as "Email sent to Surveyor"
        </Button>
      )}
    </HStack>
  );
}